import { LeftOutlined, PrinterOutlined } from '@ant-design/icons'
import { Button, Card, Flex, Space } from 'antd'
import { FormInstance } from 'antd/lib'
import dayjs from 'dayjs'
import { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useReactToPrint } from 'react-to-print'

import { useStocksServiceGetIssuanceEndpoint } from 'src/api/queries'
import {
  InquiryDocumentResponse,
  InventoryDocumentState
} from 'src/api/requests'
import { LoadingScreen } from 'src/components'
import { IssuanceBody, IssuanceHeader } from 'src/features/stock/components'
import { AdminConsumableList } from 'src/features/stock-administration/components'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { toDayjs } from 'src/utils'

type Props = {
  data?: InquiryDocumentResponse
  form: FormInstance
}

export const StockAdminIssuanceApplicationsAcceptedDetails = ({
  data,
  form
}: Props) => {
  const { t } = useLocales()
  const navigate = useNavigate()

  const { id } = useParams()

  const printableRef = useRef<HTMLDivElement>(null)

  const onPrint = useReactToPrint({
    contentRef: printableRef,
    documentTitle: `Stock_Application_${id}`
  })

  const onBackClick = () => {
    navigate(paths.stock.applications)
  }

  const { data: issuanceData, isLoading } = useStocksServiceGetIssuanceEndpoint(
    {
      id: String(data?.relatedDocuments?.[0]?.id)
    },
    [String(data?.relatedDocuments?.[0]?.id)]
  )

  useEffect(() => {
    if (issuanceData) {
      const initialFormValues = {
        showTransportInfo: !!issuanceData?.transporter,
        transporterId: issuanceData?.transporter?.id,
        takeoverDate: issuanceData?.takeoverDate
          ? dayjs(issuanceData?.takeoverDate)
          : undefined,
        accompanyingNotice: issuanceData?.accompanyingNotice,
        deliveryDate: issuanceData?.deliveryDate
          ? dayjs(issuanceData?.deliveryDate)
          : undefined,
        inquireItems: data?.items?.map((item) => ({
          name: item?.internationalName?.name,
          internationalNameId: item?.internationalName?.id,
          form: {
            name: item?.productForm?.name,
            id: item?.productForm?.id
          },
          quantity: item?.quantity,
          items: item?.beneficiaryStocks || []
        })),
        medicine: issuanceData?.items?.map((issuanceItem) => ({
          barCode: issuanceItem?.barCode,
          expirationDate: toDayjs(issuanceItem?.expirationDate),
          inquiryItemId: issuanceItem?.id,
          name: `${issuanceItem?.internationalName?.name} ${issuanceItem?.doses}`,
          quantity: issuanceItem?.quantity,
          unitPrice: issuanceItem?.unitPrice,
          currentQuantity: issuanceItem?.quantity,
          unit: {
            name: issuanceItem.productForm?.name,
            id: issuanceItem?.productForm?.id
          }
        }))
      }

      form.setFieldsValue(initialFormValues)
    }
  }, [issuanceData])

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <Flex vertical gap={24}>
      <Flex justify="space-between">
        <Button
          icon={<LeftOutlined />}
          className="fit-content"
          onClick={onBackClick}
          disabled={false}
        >
          {t('common.back')}
        </Button>

        <Space size={8}>
          <Button
            icon={<PrinterOutlined />}
            disabled={false}
            onClick={() => onPrint()}
          >
            {t('stock_applications.print')}
          </Button>
        </Space>
      </Flex>

      <Flex vertical gap={16} ref={printableRef}>
        <IssuanceHeader data={data} inquireData={issuanceData} />

        <Card>
          <Flex vertical gap={16}>
            <AdminConsumableList
              isEdit={data?.state !== InventoryDocumentState.REJECTED}
              data={data}
              issuanceData={issuanceData}
              form={form}
              isSubmitted={true}
            />

            <IssuanceBody data={data} issuanceData={issuanceData} />
          </Flex>
        </Card>
      </Flex>
    </Flex>
  )
}
